import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

export default function DesktopLinkPage() {
  useTranslation();
  const { publicCode } = useParams();
  const auth = useSelector((state) => state.auth);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);
  const [error, setError] = useState("");
  const [closeAttempted, setCloseAttempted] = useState(false);

  useEffect(() => {
    loadSession();
  }, [publicCode]);

  useEffect(() => {
    if (!approved) return undefined;

    const timeout = window.setTimeout(() => {
      setCloseAttempted(true);
      window.open("", "_self");
      window.close();
    }, 1600);

    return () => window.clearTimeout(timeout);
  }, [approved]);

  const loadSession = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/desktop-auth/session/${publicCode}`);
      setSession(res.data.session);
      if (res.data.expired) setError("This desktop sign-in request has expired. Please start again from the desktop app.");
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || "Could not load desktop sign-in request.");
    } finally {
      setLoading(false);
    }
  };

  const approve = async () => {
    try {
      setApproving(true);
      setError("");
      const config = {
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json",
        },
      };
      await axios.post("/api/desktop-auth/session/approve", JSON.stringify({ public_code: publicCode }), config);
      setApproved(true);
      await loadSession();
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || "Approval failed.");
    } finally {
      setApproving(false);
    }
  };

  const redirectPath = encodeURIComponent(`/desktop-link/${publicCode}`);
  const statusLabel = approved ? "Approved" : session?.status || "Pending";
  const deviceLabel = session?.device_name || "Unknown device";
  const platformLabel = session?.platform || "Unknown";

  return (
    <div className="auth-root">
      <div className="auth-left desktop-link-hero">
        <div className="auth-hero">
          <div className="auth-hero-badge">
            <span className="auth-hero-badge-dot" />{tr("Secure desktop sign-in")} </div>
          <h1>{tr("Approve your")} <span>Minutezilla Desktop</span> {tr("session")}</h1>
          <p>{tr("Finish the sign-in on this browser once, and the desktop app can claim a secure session without asking you to paste tokens or re-enter credentials there.")}</p>
          <div className="auth-features desktop-link-features">
            <div className="auth-feature-item">
              <div className="auth-feature-icon">1</div>
              <div className="auth-feature-text">{tr("Review the device and code before approving.")}</div>
            </div>
            <div className="auth-feature-item">
              <div className="auth-feature-icon">2</div>
              <div className="auth-feature-text">{tr("Approve this request with your existing Minutezilla account.")}</div>
            </div>
            <div className="auth-feature-item">
              <div className="auth-feature-icon">3</div>
              <div className="auth-feature-text">{tr("Return to the desktop app and continue working.")}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="auth-right desktop-link-right">
        <div className="auth-card desktop-link-card">
          <div className="desktop-link-pill">{approved ? tr("Connected") : tr("Desktop approval")}</div>
          <div className="auth-card-header">
            <h2>{tr("Approve Minutezilla Desktop")}</h2>
            <p>{tr("Link your desktop companion to your active workspace in one step.")}</p>
          </div>

          {loading && <div className="desktop-link-loading">{tr("Loading desktop sign-in request...")}</div>}

          {!loading && session && (
            <div className="desktop-link-panel">
              <div className="desktop-link-code-row">
                <span>{tr("Approval code")}</span>
                <strong>{session.public_code}</strong>
              </div>
              <div className="desktop-link-meta-grid">
                <div className="desktop-link-meta-card">
                  <span>{tr("Device")}</span>
                  <strong>{deviceLabel}</strong>
                </div>
                <div className="desktop-link-meta-card">
                  <span>{tr("Platform")}</span>
                  <strong>{platformLabel}</strong>
                </div>
                <div className="desktop-link-meta-card">
                  <span>Status</span>
                  <strong>{statusLabel}</strong>
                </div>
                <div className="desktop-link-meta-card">
                  <span>{tr("Expires")}</span>
                  <strong>{new Date(session.expires_at).toLocaleString()}</strong>
                </div>
              </div>
            </div>
          )}

          {error && <div className="alert alert-danger desktop-link-alert">{error}</div>}

          {!auth.isAuthenticated && !loading && (
            <div className="desktop-link-actions">
              <p className="desktop-link-note">{tr("Sign in first, then approve this desktop device.")}</p>
              <Link className="auth-primary-btn" to={`/login?redirect=${redirectPath}`}>{tr("Sign in to approve")}</Link>
            </div>
          )}

          {auth.isAuthenticated && !loading && !approved && !error && (
            <div className="desktop-link-actions">
              <button className="auth-primary-btn" onClick={approve} disabled={approving}>
                {approving ? tr("Approving...") : tr("Approve this desktop device")}
              </button>
              <p className="desktop-link-note">{tr("This only approves the currently shown desktop request.")}</p>
            </div>
          )}

          {approved && (
            <div className="desktop-link-actions">
              <div className="alert alert-success desktop-link-alert">{tr("Desktop approved. The desktop app can finish sign-in now.")} </div>
              <p className="desktop-link-note">{tr("This tab will try to close automatically. If it stays open, you can close it manually and return to the desktop app.")} </p>
              {closeAttempted && (
                <button className="desktop-link-secondary-btn" onClick={() => window.close()}>{tr("Close this tab")} </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
